import React from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Button from '@atlaskit/button/standard-button';
import CloseIcon from '@atlaskit/icon/glyph/editor/close';
import UnlinkIcon from '@atlaskit/icon/glyph/unlink';
import {
  ModalTransition,
  ModalHeader,
  ModalTitle,
} from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import Tooltip from '@atlaskit/tooltip';

import Modal from 'src/components/modal-dialog-actions';
import { LoadingStatus } from 'src/constants/loading-status';
import { UserName } from 'src/containers/user-name';
import { useIntl } from 'src/hooks/intl';
import {
  closeManageProjectsDialog,
  fetchJiraRelevantProjects,
  openUnlinkProjectDialog,
} from 'src/redux/jira/actions';
import {
  getIsManageProjectsDialogOpen,
  getRelevantProjectFetchedStatus,
  getProjectAssociations,
} from 'src/redux/jira/selectors/repo-page-selectors';
import { ProjectAssociation } from 'src/redux/jira/types';
import { SubtleLink } from 'src/styles';

import { ErrorMessageWithRetry } from '../error-message-with-retry';
import { JiraProjectCard } from '../jira-project-card';

import { LinkProject } from './link-project';
import messages from './manage-projects-dialog.i18n';
import * as styles from './manage-projects-dialog.styled';
import { UnlinkProjectDialog } from './unlink-project-dialog';

export const ManageProjectsDialog = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isOpen = useSelector(getIsManageProjectsDialogOpen);
  const rawProjectAssociations = useSelector(getProjectAssociations);
  const relevantProjectsFetchedStatus = useSelector(
    getRelevantProjectFetchedStatus
  );

  // Sort project associations by project name
  const projectAssociations = rawProjectAssociations.sort((a, b) =>
    a.project.name.localeCompare(b.project.name)
  );

  const isLoading = relevantProjectsFetchedStatus === LoadingStatus.Fetching;
  const isFailed = relevantProjectsFetchedStatus === LoadingStatus.Failed;
  const isSuccess = relevantProjectsFetchedStatus === LoadingStatus.Success;

  const Header = () => (
    <ModalHeader>
      <ModalTitle>
        {intl.formatMessage(messages.manageProjectsDialogHeading)}
      </ModalTitle>
      <Button
        onClick={() => dispatch(closeManageProjectsDialog())}
        spacing="none"
        appearance="subtle"
        iconBefore={
          <CloseIcon
            label={intl.formatMessage(messages.closeManageProjectsDialogLabel)}
          />
        }
      />
    </ModalHeader>
  );

  const linkedProjectLabel = (association: ProjectAssociation) => (
    <styles.LinkedByText>
      {association.associatedBy ? (
        <FormattedMessage
          {...messages.manuallyLinkedProjectLabel}
          values={{
            personName: (
              <UserName
                key={`project-${association.project.id}`}
                linkComponent={SubtleLink}
                profileCardPosition="bottom-start"
                user={association.associatedBy}
              />
            ),
          }}
        />
      ) : (
        intl.formatMessage(messages.automaticallyLinkedProjectLabel)
      )}
    </styles.LinkedByText>
  );

  return (
    <>
      <ModalTransition>
        {isOpen && (
          <Modal
            components={{
              Header,
            }}
            onClose={() => dispatch(closeManageProjectsDialog())}
          >
            <p>{intl.formatMessage(messages.manageProjectsDialogDetails)}</p>

            <styles.LinkedProjectsContainer
              hasProjects={!!projectAssociations.length}
            >
              <styles.SubTitle>
                {intl.formatMessage(messages.manuallyLinkProjectTitle)}
              </styles.SubTitle>
              <LinkProject
                relevantProjects={projectAssociations.map(
                  association => association.project
                )}
              />

              {isLoading && (
                <styles.AlignCenter>
                  <Spinner />
                </styles.AlignCenter>
              )}
              {isFailed && (
                <styles.AlignCenter>
                  <ErrorMessageWithRetry
                    onRetry={() => dispatch(fetchJiraRelevantProjects())}
                    errorMessage={intl.formatMessage(
                      messages.errorLoadingProjectsMessage
                    )}
                  />
                </styles.AlignCenter>
              )}

              {isSuccess && projectAssociations.length > 0 && (
                <>
                  <styles.SubTitle>
                    {intl.formatMessage(messages.linkedProjectsTitle)}
                  </styles.SubTitle>

                  {projectAssociations.map(association => (
                    <styles.RelevantProject key={association.project.id}>
                      <JiraProjectCard project={association.project} />
                      <styles.UnlinkAction>
                        {linkedProjectLabel(association)}
                        <Tooltip
                          content={intl.formatMessage(
                            messages.unlinkProjectLabel
                          )}
                        >
                          <Button
                            appearance="subtle"
                            spacing="none"
                            iconBefore={
                              <UnlinkIcon
                                label={intl.formatMessage(
                                  messages.unlinkProjectLabel
                                )}
                              />
                            }
                            onClick={() =>
                              dispatch(
                                openUnlinkProjectDialog(association.project)
                              )
                            }
                            testId="manage-projects-dialog-unlink-action"
                          />
                        </Tooltip>
                      </styles.UnlinkAction>
                    </styles.RelevantProject>
                  ))}
                </>
              )}
            </styles.LinkedProjectsContainer>
          </Modal>
        )}
      </ModalTransition>
      <UnlinkProjectDialog />
    </>
  );
};
